/**
 * Anchor registry for the FTUE engine (PEL-515).
 *
 * Steps reference their target by a stable `anchorId` string rather than by ref,
 * which decouples step definitions (and the renderer) from the page components
 * that own the DOM. A page registers its element via the `useFtueAnchor` hook;
 * the host resolves the active step's `anchorId` to a live element and re-renders
 * reactively as anchors mount/unmount (so an event-gated coachmark can resolve
 * the moment its target appears, and fall back when it is absent).
 *
 * One id can map to MORE THAN ONE element at a time, for two distinct reasons:
 *
 *  1. **Dual-Outlet shell (PEL-515).** The app shell renders each routed page
 *     through two `<Outlet/>`s (a `hidden md:flex` desktop tree and a `md:hidden`
 *     mobile tree), so a page registers an anchor twice — once per tree, one of
 *     which is `display:none`. The `display:none` copy is connected but has no
 *     client rects, so we resolve to the element that is actually rendered.
 *
 *  2. **Surface-adaptive overlays (PEL-520).** The job-detail view opens as an
 *     overlay/drawer OVER the Opportunities list (not a route swap), so the list
 *     stays mounted and visible behind it. A concept-based step registers the
 *     same anchor id on both the list card and the detail overlay; both pass the
 *     "is rendered" check, so visibility alone would let the step anchor to the
 *     card hiding behind the drawer. We therefore tag each registration with a
 *     LAYER and resolve to the highest-priority rendered layer (`overlay` beats
 *     `base`). When the overlay mounts/unmounts its copy, the registry emits and
 *     the host re-anchors to the now-active surface (spec §7).
 */

import { useCallback, useRef, useSyncExternalStore } from 'react'

type Listener = () => void

/** Stacking layer an anchor lives on. Higher priority wins at resolve time. */
export type FtueLayer = 'base' | 'overlay'
const LAYER_PRIORITY: Record<FtueLayer, number> = { base: 0, overlay: 1 }

const anchors = new Map<string, Set<HTMLElement>>()
/** Layer per registered element; absent => `base`. */
const elementLayers = new WeakMap<HTMLElement, FtueLayer>()
const listeners = new Set<Listener>()

function emit(): void {
  for (const listener of listeners) listener()
}

/** Register an element under an anchor id. Idempotent per element. */
export function registerAnchor(id: string, el: HTMLElement, layer: FtueLayer = 'base'): void {
  // Layer can change for an already-tracked element (re-registration is a no-op
  // for the set, but the layer must stay current).
  elementLayers.set(el, layer)
  let set = anchors.get(id)
  if (!set) {
    set = new Set()
    anchors.set(id, set)
  }
  if (set.has(el)) return
  set.add(el)
  emit()
}

/** Remove a specific element from an anchor id. */
export function unregisterAnchor(id: string, el: HTMLElement): void {
  const set = anchors.get(id)
  if (!set || !set.has(el)) return
  set.delete(el)
  elementLayers.delete(el)
  if (set.size === 0) anchors.delete(id)
  emit()
}

/**
 * An element is resolvable only if it is actually rendered. A `display:none`
 * element (e.g. the inactive responsive layout's copy) is connected but has no
 * client rects, so we skip it in favour of the visible copy.
 */
function isRendered(el: HTMLElement): boolean {
  return el.isConnected && el.getClientRects().length > 0
}

function layerPriority(el: HTMLElement): number {
  return LAYER_PRIORITY[elementLayers.get(el) ?? 'base']
}

/** The stacking layer a (currently-resolved) element was registered on. */
export function getElementLayer(el: HTMLElement | null | undefined): FtueLayer {
  return el ? (elementLayers.get(el) ?? 'base') : 'base'
}

export function getAnchor(id: string | null | undefined): HTMLElement | null {
  if (!id) return null
  const set = anchors.get(id)
  if (!set || set.size === 0) return null

  let best: HTMLElement | null = null
  let bestPriority = Number.NEGATIVE_INFINITY
  for (const el of set) {
    if (!isRendered(el)) continue
    const priority = layerPriority(el)
    // Prefer the highest layer (overlay > base); within a layer, `>=` lets a
    // later registration win — preserving the original last-writer tiebreak and
    // re-anchoring to whichever copy mounted most recently.
    if (best === null || priority >= bestPriority) {
      best = el
      bestPriority = priority
    }
  }
  // A registered-but-hidden element is not a usable anchor. Returning it would
  // make the host render a zero-sized spotlight and, for modal steps, a blocker
  // over the entire viewport. Keep the surface dormant until layout produces a
  // genuinely rendered copy.
  return best
}

/** Subscribe to anchor mount/unmount changes (for useSyncExternalStore). */
export function subscribeAnchors(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Reactively resolve an anchor id to its live element (re-renders as anchors
 * mount/unmount). The host uses this via `useFtue`; surfaces rendered outside the
 * engine (the repeatable app-notice coachmark) use it directly so they can point
 * at the same anchors without reaching into the registry internals.
 */
export function useFtueAnchorElement(anchorId: string | null | undefined): HTMLElement | null {
  return useSyncExternalStore(subscribeAnchors, () => getAnchor(anchorId))
}

/**
 * Returns a ref callback that registers a DOM element under `anchorId`.
 * Spread onto any element/component that forwards its ref:
 *   <div ref={useFtueAnchor('sidebar-scan-agents')} />
 *
 * Pass `layer: 'overlay'` for an anchor that lives on a surface stacked above the
 * base page (the job-detail drawer), so a concept-based step re-anchors to it
 * while it is open instead of the list element behind it (PEL-520, spec §7):
 *   <div ref={useFtueAnchor('opp-action-band', 'overlay')} />
 *
 * Each hook instance tracks the element it last registered so it can remove
 * exactly that one on unmount/replacement — supporting multiple live elements
 * per id (see module doc).
 */
export function useFtueAnchor(
  anchorId: string,
  layer: FtueLayer = 'base',
): (el: HTMLElement | null) => void {
  const prev = useRef<HTMLElement | null>(null)
  return useCallback(
    (el: HTMLElement | null) => {
      if (prev.current && prev.current !== el) unregisterAnchor(anchorId, prev.current)
      prev.current = el
      if (el) registerAnchor(anchorId, el, layer)
    },
    [anchorId, layer],
  )
}
